import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Container, Row, Col, Button, Badge, Spinner } from 'react-bootstrap'
import ResizableTable from '../components/ResizableTable'
import PaginationComponent from '../components/Pagination'
import SearchInputWithClear from '../components/SearchInputWithClear'
import CopyableText from '../components/CopyableText'
import { amazonAPI } from '../api/amazonAPI'
import { formatDateTime, formatCurrency } from '../utils/commonUtils'
import { formatErrorResponse } from '../utils/errorHandlers'
import { createColumnToggleDropdown, useTableState } from '../utils/tableUtils'

function AmazonInventoryPlanningDetailScreen() {
    const { reportId } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const [returnFilters] = useState(location.state?.returnFilters || '')

    const [report, setReport] = useState(null)
    const [reportLoading, setReportLoading] = useState(true)
    const [error, setError] = useState('')

    const [rowsData, setRowsData] = useState([])
    const [rowsLoading, setRowsLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [searchInputValue, setSearchInputValue] = useState('')
    const debounceTimeoutRef = useRef(null)
    const [sortColumn, setSortColumn] = useState(null)
    const [sortDirection, setSortDirection] = useState(null)
    const [currentPage, setCurrentPage] = useState(1)
    const [totalItems, setTotalItems] = useState(0)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())

    const initialTableState = {
        visibility: {
            sku: true, asin: true, productName: true,
            inventoryHealthStatus: true, daysOfSupply: true,
            salesLast30Days: true, availableQuantity: true,
            yourPrice: true, salePrice: true,
            lowInventoryFee: true, recommendedAction: true,
        },
        widths: {}
    }
    const { columnState, updateColumnVisibility } = useTableState('amazon_inventory_planning', initialTableState)
    const columnVisibility = columnState.visibility || initialTableState.visibility
    const toggleColumnVisibility = (columnId) => updateColumnVisibility(columnId, !columnVisibility[columnId])
    const columnLabels = {
        sku: 'SKU', asin: 'ASIN', productName: 'Product Name',
        inventoryHealthStatus: 'Health Status', daysOfSupply: 'Days of Supply',
        salesLast30Days: 'Sales (30d)', availableQuantity: 'Available Qty',
        yourPrice: 'Your Price', salePrice: 'Sale Price',
        lowInventoryFee: 'Low Inv. Fee', recommendedAction: 'Recommended Action',
    }

    const healthStatusBadge = (status) => {
        if (!status) return ''
        const bgMap = {
            'Healthy': 'success',
            'Watch': 'warning',
            'Critical': 'danger',
        }
        return <Badge bg={bgMap[status] || 'secondary'}>{status}</Badge>
    }

    useEffect(() => {
        const fetchReport = async () => {
            setReportLoading(true)
            try {
                const data = await amazonAPI.inventoryPlanning.get(reportId)
                setReport(data)
            } catch (err) {
                setError(formatErrorResponse(err))
            } finally {
                setReportLoading(false)
            }
        }
        fetchReport()
    }, [reportId])

    const fetchRows = useCallback(async () => {
        if (!report) return
        setRowsLoading(true)
        setError('')
        try {
            const params = {
                page: currentPage,
                pageSize: pageSize,
            }
            if (searchTerm) params.searchTerm = searchTerm
            if (sortColumn) {
                params.sortColumn = sortColumn
                params.sortDirection = sortDirection || 'asc'
            }

            const data = await amazonAPI.inventoryPlanning.getRows(reportId, params)
            if (data && data.results !== undefined) {
                setRowsData(data.results)
                setTotalItems(data.count)
            } else {
                setRowsData(data || [])
                setTotalItems((data || []).length)
            }
        } catch (err) { setError(formatErrorResponse(err)) }
        finally { setRowsLoading(false) }
    }, [report, reportId, currentPage, pageSize, searchTerm, sortColumn, sortDirection])

    useEffect(() => { fetchRows() }, [fetchRows])

    const handleSearch = (e) => {
        const value = e.target.value
        setSearchInputValue(value)
        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current)
        debounceTimeoutRef.current = setTimeout(() => { setSearchTerm(value); setCurrentPage(1) }, 300)
    }
    const handleClearSearch = () => { setSearchInputValue(''); setSearchTerm(''); setCurrentPage(1) }

    const handleSort = (column, direction) => { setSortColumn(column); setSortDirection(direction); setCurrentPage(1) }

    const columns = [
        columnVisibility.sku && { id: 'sku', header: 'SKU', sortable: true, cell: (row) => row.sku },
        columnVisibility.asin && { id: 'asin', header: 'ASIN', sortable: true, cell: (row) => row.asin ? <CopyableText value={row.asin} toastLabel="ASIN" searchTerm={searchTerm} /> : '' },
        columnVisibility.productName && { id: 'productName', header: 'Product Name', sortable: true, cell: (row) => row.product_name || '' },
        columnVisibility.inventoryHealthStatus && { id: 'inventoryHealthStatus', header: 'Health Status', sortable: true, cell: (row) => healthStatusBadge(row.inventory_health_status) },
        columnVisibility.daysOfSupply && { id: 'daysOfSupply', header: 'Days of Supply', sortable: true, cell: (row) => row.days_of_supply != null ? row.days_of_supply : '' },
        columnVisibility.salesLast30Days && { id: 'salesLast30Days', header: 'Sales (30d)', sortable: true, cell: (row) => row.sales_last_30_days != null ? row.sales_last_30_days : '' },
        columnVisibility.availableQuantity && { id: 'availableQuantity', header: 'Available Qty', sortable: true, cell: (row) => row.available_quantity },
        columnVisibility.yourPrice && { id: 'yourPrice', header: 'Your Price', sortable: true, cell: (row) => row.your_price != null ? formatCurrency(row.your_price) : '' },
        columnVisibility.salePrice && { id: 'salePrice', header: 'Sale Price', sortable: true, cell: (row) => row.sale_price != null ? formatCurrency(row.sale_price) : '' },
        columnVisibility.lowInventoryFee && { id: 'lowInventoryFee', header: 'Low Inv. Fee', sortable: true, cell: (row) => row.low_inventory_fee_applied ? <Badge bg="danger">Yes</Badge> : '' },
        columnVisibility.recommendedAction && { id: 'recommendedAction', header: 'Recommended Action', sortable: true, cell: (row) => row.recommended_action || '' },
    ].filter(Boolean)

    if (reportLoading) {
        return (
            <Container fluid className="px-4 py-4">
                <div className="text-center py-4"><Spinner animation="border" /></div>
            </Container>
        )
    }

    const headerSubtitle = report
        ? `${report.seller_account_name || ''} (${formatDateTime(report.requested_at)})`
        : ''

    return (
        <Container fluid className="px-4 py-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h1 className="mb-0">Inventory Planning</h1>
                    {headerSubtitle && <p className="text-muted mb-0">{headerSubtitle}</p>}
                </div>
                <Button variant="outline-primary" onClick={() => navigate('/amazon-inventory-planning', { state: { returnFilters } })}>
                    <i className="fas fa-arrow-left me-2"></i>Back to List
                </Button>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            {rowsLoading && rowsData.length === 0 ? (
                <div className="text-center py-4"><Spinner animation="border" /></div>
            ) : (
                <>
                    <div className="d-flex align-items-center mb-3">
                        <div>{createColumnToggleDropdown(columnVisibility, columnLabels, toggleColumnVisibility)}</div>
                        <div className="ms-auto" style={{ maxWidth: '300px' }}>
                            <SearchInputWithClear value={searchInputValue} onChange={handleSearch}
                                onClear={handleClearSearch} placeholder="Search SKU, ASIN, or Name..." />
                        </div>
                    </div>
                    {rowsData.length === 0 ? (
                        <p className="text-muted">No inventory planning data found.</p>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <ResizableTable columns={columns} data={rowsData} tableId="amazon_inventory_planning"
                                    enableSorting={true} onSort={handleSort}
                                    sortColumn={sortColumn} sortDirection={sortDirection}
                                    initialColumnWidths={{
                                        sku: 150, asin: 130, productName: 250,
                                        inventoryHealthStatus: 120, daysOfSupply: 120,
                                        salesLast30Days: 110, availableQuantity: 120,
                                        yourPrice: 100, salePrice: 100,
                                        lowInventoryFee: 110, recommendedAction: 200,
                                    }}
                                    striped hover className="table-sm" />
                            </div>
                            <div className="mt-3">
                                <PaginationComponent
                                    currentPage={currentPage}
                                    totalItems={totalItems}
                                    onPageChange={setCurrentPage}
                                    onItemsPerPageChange={(size) => { setPageSize(size); setCurrentPage(1) }}
                                />
                            </div>
                        </>
                    )}
                </>
            )}
        </Container>
    )
}

export default AmazonInventoryPlanningDetailScreen
